import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AudioPlaylistCard } from "../components/AudioPlaylistCard";
import { ChapterQuestionsTable } from "../components/ChapterQuestionsTable";
import { DashboardAlert } from "../components/DashboardAlert";
import { DashboardProgressBar } from "../components/DashboardProgressBar";
import { DashboardTopBar } from "../components/DashboardTopBar";
import { GeneratedChaptersTable } from "../components/GeneratedChaptersTable";
import { GiveBooksCard } from "../components/GiveBooksCard";
import { StartEditingInfosModal } from "../components/Modals/StartEditingInfosModal";
import { WelcomeInfosModal } from "../components/Modals/WelcomeInfosModal";
import { PDFGeneratedState } from "../components/PDFGeneratedState";
import { BookContext } from "../contexts/BookContext";

export const Dashboard = () => {
  // Hooks ===============================================
  const { t } = useTranslation();

  // Local Storage ========================================
  const welcomeInfosModalSeen = !!localStorage.getItem("welcomeInfosModalSeen");
  const startEditingInfosModalSeen = !!localStorage.getItem(
    "startEditingInfosModalSeen"
  );

  // Context =============================================
  const bookContext = useContext(BookContext);
  const status = bookContext.book ? bookContext.book.status : null;
  const generatedChapters = bookContext.generated_chapters;
  const audioChapters = bookContext.book?.audio_chapters || [];

  // States ===============================================
  const [renderKey, setRenderKey] = useState(0);
  const [isStartEditingModalOpen, setIsStartEditingModalOpen] =
    useState<boolean>(
      (!startEditingInfosModalSeen && status === "ai_generated") || false
    );
  const [isWelcomeState, setIsWelcomeState] = useState<boolean>(
    !welcomeInfosModalSeen
  );
  const [isFinishedState, setIsFinishedState] = useState<boolean>(false);

  // Effects =============================================
  useEffect(() => {
    setRenderKey((prev) => prev + 1);
  }, [bookContext.answers, bookContext.questions]);

  useEffect(() => {
    if (
      status &&
      [
        "pdf_generated",
        "audio_generating",
        "audio_generated",
        "audio_validated",
      ].includes(status)
    ) {
      setIsFinishedState(true);
    }
    if (status === "ai_generated" && !startEditingInfosModalSeen) {
      setIsStartEditingModalOpen(true);
    }
  }, [status]);

  return (
    <div
      key={renderKey}
      className="w-full px-4 sm:px-6 lg:px-12 py-4 sm:py-6 lg:py-8 flex flex-1 flex-col gap-y-4 sm:gap-y-6"
    >
      {/* Alerts */}
      {status === "pdf_generating" && (
        <DashboardAlert
          title={t("PDFGenAlert.attention")}
          body={t("PDFGenAlert.attention2", {
            interpolation: {
              escapeValue: false,
            },
          })}
        />
      )}

      {status === "ai_generating" && (
        <DashboardAlert
          title={t("AIGenAlert.attention")}
          body={t("AIGenAlert.attention2", {
            interpolation: {
              escapeValue: false,
            },
          })}
        />
      )}

      {status === "audio_generating" && (
        <DashboardAlert
          title={t("AudioGenAlert.attention")}
          body={t("AudioGenAlert.attention2", {
            interpolation: {
              escapeValue: false,
            },
          })}
        />
      )}

      {/* Extra books bought for someone else */}
      <GiveBooksCard />

      {/* Dashboard Welcome State */}
      {isWelcomeState && (
        <WelcomeInfosModal open={isWelcomeState} setOpen={setIsWelcomeState} />
      )}

      {/* Modal Start Editing Info */}
      {isStartEditingModalOpen && (
        <StartEditingInfosModal
          open={isStartEditingModalOpen}
          setOpen={setIsStartEditingModalOpen}
        />
      )}

      {/* Top Bar */}
      {status &&
        ![
          "pdf_generated",
          "audio_generating",
          "audio_generated",
          "audio_validated",
        ].includes(status) && <DashboardTopBar />}

      {/* Dashboard Finished State */}
      {bookContext.book && isFinishedState && <PDFGeneratedState />}

      {audioChapters.length > 0 &&
        bookContext.book?.status === "audio_validated" && (
          <AudioPlaylistCard
            playlist={audioChapters}
            title={t("dashboard.my_audio_book")}
          />
        )}

      {/* ProgressBar */}
      {status && ["draft", "ready_to_generate"].includes(status) && (
        <DashboardProgressBar />
      )}

      {/* Chapters for Questions */}
      {status && ["draft", "ready_to_generate"].includes(status) && (
        <ChapterQuestionsTable />
      )}

      {/* Generated Chapters */}
      {status &&
        ["ai_generated", "completed"].includes(status) &&
        generatedChapters.length !== 0 && <GeneratedChaptersTable />}
    </div>
  );
};
